import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ModalShell } from "@/components/ui/ModalShell";
import { Button } from "@/components/ui/Button";
import { ArrowRight, Clock, Sparkles, X } from "lucide-react";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { cn } from "@/lib/cn";
import { useTranslation } from "@/i18n/I18nProvider";
import { isDiscountLive, isOneTimeEnded } from "@/hooks/useActiveDiscount";
import { useTaxonomy } from "@/data/TaxonomyProvider";
import type { Lang } from "@/i18n/translations";
import type { Discount, Restaurant, RestaurantId } from "@/types/restaurant";
import { formatOfferDays } from "./OfferCard";
import { ReportLink } from "./ReportLink";

export interface OffersListProps {
  restaurants: Restaurant[];
  discountsByRestaurant: Record<RestaurantId, Discount[]>;
  now: Date;
  /** City label used in the empty state. */
  cityLabel: string;
  /** Opens a restaurant's detail modal from an offer's "View venue" button. */
  onSelectRestaurant: (restaurant: Restaurant) => void;
}

interface OfferEntry {
  discount: Discount;
  restaurant: Restaurant;
}

/**
 * The Offers & Events tab: every active offer in the city as a grid of square
 * tiles, live-now entries first. Tapping a tile opens a popup with the full
 * description, the venue's name + tagline, and a link through to the venue.
 */
export function OffersList({
  restaurants,
  discountsByRestaurant,
  now,
  cityLabel,
  onSelectRestaurant,
}: OffersListProps) {
  const { t, lang } = useTranslation();
  const { tagLabels } = useTaxonomy();
  const [open, setOpen] = useState<OfferEntry | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  useFocusTrap(popupRef, open !== null);

  const offers = useMemo(() => {
    const flat = restaurants.flatMap((restaurant) =>
      (discountsByRestaurant[restaurant.id] ?? [])
        .filter((discount) => !isOneTimeEnded(discount, now))
        .map((discount) => ({ discount, restaurant })),
    );
    return flat.sort((a, b) => {
      const liveA = isDiscountLive(a.discount, now);
      const liveB = isDiscountLive(b.discount, now);
      if (liveA !== liveB) return liveA ? -1 : 1;
      return a.restaurant.name.localeCompare(b.restaurant.name);
    });
  }, [restaurants, discountsByRestaurant, now]);

  // Escape + background-scroll lock while the popup is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(null);
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (offers.length === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-white/15 light:border-black/15 bg-zinc-900/30 light:bg-white/50 p-10 text-center sm:mt-10">
        <p className="text-sm text-zinc-300 light:text-zinc-700">{t("OFFERS_EMPTY", { city: cityLabel })}</p>
      </div>
    );
  }

  const popupLive = open ? isDiscountLive(open.discount, now) : false;

  return (
    <>
      <div className="mx-auto mt-6 grid max-w-3xl grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-2">
        {offers.map(({ discount, restaurant }, i) => {
          const live = isDiscountLive(discount, now);
          return (
            <OfferTile
              key={discount.id}
              discount={discount}
              restaurant={restaurant}
              live={live}
              featured={i === 0 && live}
              lang={lang}
              everyDay={t("OFFER_EVERY_DAY")}
              liveLabel={t("OFFER_LIVE")}
              onOpen={() => setOpen({ discount, restaurant })}
            />
          );
        })}
      </div>

      <AnimatePresence>
        {/* `rise` motion — scaling the clipped container re-rasterizes the
            offer image edge and flashes a seam (same as the detail modal). */}
        {open && (
          <ModalShell
            key="offer-popup"
            motionStyle="rise"
            onBackdropClick={() => setOpen(null)}
            panelRef={popupRef}
            panelClass="max-h-[90dvh] max-w-md overflow-y-auto"
          >
              <button
                type="button"
                onClick={() => setOpen(null)}
                className="absolute right-3 top-3 z-20 grid h-9 w-9 place-items-center rounded-full border border-white/15 light:border-black/15 bg-zinc-900/80 light:bg-white/80 text-zinc-300 light:text-zinc-700 backdrop-blur transition hover:border-white/50 light:hover:border-black/40 hover:text-zinc-50 light:hover:text-zinc-900"
                aria-label={t("CLOSE")}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>

              {open.discount.image_url && (
                <img
                  src={open.discount.image_url}
                  alt=""
                  className="aspect-[16/10] w-full rounded-t-2xl object-cover sm:rounded-t-3xl"
                />
              )}

              <div className="space-y-4 p-5">
                <div className="space-y-2">
                  {popupLive ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-2xs font-medium text-emerald-300 light:text-emerald-700">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" aria-hidden />
                      {t("OFFER_LIVE")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border border-zinc-800 light:border-black/10 bg-zinc-900/60 light:bg-white/70 px-2 py-0.5 text-2xs text-zinc-400 light:text-zinc-600">
                      <Clock className="h-3 w-3 text-dim light:text-zinc-600" aria-hidden />
                      {open.discount.starts_at}–{open.discount.ends_at}
                      <span className="text-zinc-600">
                        · {formatOfferDays(open.discount.days_of_week, lang, t("OFFER_EVERY_DAY"))}
                      </span>
                    </span>
                  )}
                  <h3 className="text-lg font-semibold tracking-tight text-zinc-50 light:text-zinc-900">
                    {open.discount.info}
                  </h3>
                </div>

                {open.discount.description?.trim() && (
                  <p className="whitespace-pre-line text-sm leading-relaxed text-zinc-300 light:text-zinc-700">
                    {open.discount.description}
                  </p>
                )}

                <div className="rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 p-3 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset]">
                  <p className="text-sm font-medium text-zinc-100 light:text-zinc-900">{open.restaurant.name}</p>
                  {tagLabels(open.restaurant.tag_slugs, lang).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {tagLabels(open.restaurant.tag_slugs, lang).slice(0, 4).map((label) => (
                        <span
                          key={label}
                          className="rounded-md border border-white/[0.12] light:border-black/10 bg-zinc-800/60 light:bg-zinc-200/60 px-1.5 py-0.5 text-2xs text-zinc-300 light:text-zinc-700"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => {
                    const restaurant = open.restaurant;
                    setOpen(null);
                    onSelectRestaurant(restaurant);
                  }}
                  className="group/cta w-full"
                >
                  {t("OFFER_VIEW_VENUE")}
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-200 group-hover/cta:translate-x-0.5"
                    aria-hidden
                  />
                </Button>

                <div className="flex justify-end">
                  <ReportLink
                    label={`${open.restaurant.name}: ${open.discount.info}`}
                    target={{
                      kind: "venue",
                      restaurantId: open.restaurant.id,
                      restaurantName: open.restaurant.name,
                      offerId: open.discount.id,
                      offerInfo: open.discount.info,
                    }}
                    onRequest={() => setOpen(null)}
                  />
                </div>
              </div>
          </ModalShell>
        )}
      </AnimatePresence>
    </>
  );
}

interface OfferTileProps {
  discount: Discount;
  restaurant: Restaurant;
  live: boolean;
  /** First live offer renders as a full-width landscape "marquee" tile. */
  featured?: boolean;
  lang: Lang;
  everyDay: string;
  liveLabel: string;
  onOpen: () => void;
}

function OfferTile({ discount, restaurant, live, featured, lang, everyDay, liveLabel, onOpen }: OfferTileProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        // No hover transform: lifting the tile re-rasterizes the rounded clip
        // and flashes a fringe at the image seam. The "light under the tile"
        // is a deliberate warm glow in the hover shadow instead, eased slowly.
        // (Hover-only, so it never runs on touch scroll; an opacity overlay
        // can't replace it — overflow-hidden would clip the outer glow.)
        "group flex flex-col overflow-hidden rounded-2xl border border-white/[0.12] light:border-black/10 bg-zinc-900/50 light:bg-white/60 text-left shadow-[0_1px_0_rgba(255,255,255,0.05)_inset] transition-[border-color,box-shadow] duration-500 ease-out hover:border-white/30 light:hover:border-black/30 hover:shadow-[0_1px_0_rgba(255,255,255,0.07)_inset,0_24px_48px_-20px_rgba(245,241,232,0.16)]",
        featured && "sm:col-span-2",
      )}
    >
      <div
        className={cn(
          "relative w-full overflow-hidden bg-zinc-800 light:bg-zinc-200",
          featured ? "aspect-[16/9] sm:aspect-[21/9]" : "aspect-square",
        )}
      >
        {discount.image_url ? (
          <img
            src={discount.image_url}
            alt=""
            className="h-full w-full object-cover transition-transform duration-700 ease-out will-change-transform motion-safe:group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <Sparkles className="h-8 w-8 text-zinc-600" aria-hidden />
          </div>
        )}
        {/* Venue name sits on the image, poster-style. */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-zinc-950/85 via-zinc-950/30 to-transparent p-2.5 pt-8">
          <p className="truncate font-mono text-2xs uppercase tracking-[0.18em] text-zinc-50 light:text-zinc-900 drop-shadow-[0_1px_6px_rgba(0,0,0,0.85)]">
            {restaurant.name}
          </p>
        </div>
        {live && (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-950/70 px-2 py-0.5 text-2xs font-medium text-emerald-300 backdrop-blur">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" aria-hidden />
            {liveLabel}
          </span>
        )}
      </div>
      {/* Perforated ticket seam — punched notches get clipped into half-circle
          bites by the tile's overflow-hidden. */}
      <div aria-hidden className="relative">
        <span className="absolute -left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-background" />
        <span className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-background" />
        <div className="mx-3 border-t border-dashed border-white/20 light:border-black/15" />
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <p
          className={cn(
            "line-clamp-2 text-zinc-100 light:text-zinc-900",
            featured ? "font-display text-[1.05rem] leading-snug tracking-tight" : "text-sm font-medium",
          )}
        >
          {discount.info}
        </p>
        {!live && (
          <p className="mt-auto flex flex-wrap items-center gap-1 pt-1 text-2xs text-zinc-400 light:text-zinc-600">
            <Clock className="h-3 w-3 text-dim light:text-zinc-600" aria-hidden />
            {discount.starts_at}–{discount.ends_at}
            <span className="text-zinc-600">· {formatOfferDays(discount.days_of_week, lang, everyDay)}</span>
          </p>
        )}
      </div>
    </button>
  );
}
